import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, ButtonLink, Spinner } from '../../components/ui';
import { ApiError, api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { usePageTitle } from '../../lib/usePageTitle';
import { AuthShell } from './AuthShell';

export function Activate() {
  usePageTitle('Activating your account — WaveQA');

  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { signIn } = useAuth();

  const [error, setError] = useState<ApiError | null>(null);
  const token = params.get('token');

  // React 18+ mounts effects twice in development StrictMode. The activation
  // token is single-use, so a second call would report "already used" on a
  // perfectly good link - this guard makes the redemption happen once.
  const attempted = useRef(false);

  useEffect(() => {
    if (!token || attempted.current) return;
    attempted.current = true;

    void (async () => {
      try {
        const session = await api.activate(token);
        signIn(session);
        // Redeeming the emailed link already proves control of the mailbox,
        // which is the same evidence the OTP step collects - so the user lands
        // signed in rather than being bounced to a login form.
        navigate('/app', { replace: true });
      } catch (err) {
        setError(err instanceof ApiError ? err : new ApiError('Something went wrong.', 0));
      }
    })();
  }, [token, signIn, navigate]);

  if (!token) {
    return (
      <AuthShell title="Activation link incomplete">
        <Alert tone="error">
          This link is missing its activation token. Open the link from your
          email directly rather than copying part of it.
        </Alert>
        <ButtonLink to="/login" variant="secondary" className="w-full">
          Go to sign in
        </ButtonLink>
      </AuthShell>
    );
  }

  if (error) {
    return (
      <AuthShell
        title="That link did not work"
        subtitle="Activation links expire after 24 hours and can only be used once."
      >
        <Alert tone="error">{error.message}</Alert>
        <p className="mb-5 text-sm text-hull-300">
          If you already activated this account, simply sign in. Otherwise
          register again to receive a fresh link.
        </p>
        <div className="flex gap-2">
          <ButtonLink to="/login" className="flex-1">
            Sign in
          </ButtonLink>
          <ButtonLink to="/register" variant="secondary" className="flex-1">
            Register
          </ButtonLink>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Activating your account">
      <div className="flex items-center justify-center py-6">
        <Spinner label="Confirming your email address" />
      </div>
      <p className="text-center text-xs text-hull-500">
        Taking too long?{' '}
        <Link to="/login" className="text-surge-400">
          Go to sign in
        </Link>
      </p>
    </AuthShell>
  );
}
